import React, { useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Group,
  Paper,
  Stack,
  Text,
  Textarea,
} from '@mantine/core';
import type { ProjectDetail } from '@tinypro-audit/contracts';
import { z } from 'zod';
import { useRevalidator } from 'react-router-dom';
import { fetchJson } from '../api/client';
import { projectReviewModel, type ReviewOrder } from './project-review-model';

export function OrderReviewCompletion({
  data,
  order,
}: {
  data: ProjectDetail;
  order: ReviewOrder;
}) {
  const [editingBasis, setEditingBasis] = useState('');
  const [editing, setEditing] = useState(false),
    [note, setNote] = useState(''),
    [busy, setBusy] = useState(false),
    [error, setError] = useState('');
  const revalidator = useRevalidator();
  const model = projectReviewModel(data);
  const result = order.result!;
  const open = data.workflow.exceptions.filter(
    (e) =>
      (e.order_id === order.id || !e.order_id) &&
      e.state !== 'resolved' &&
      (e.content.attention !== 'information' || e.review_decision),
  );
  const completed =
    model.current && order.review_completion?.action === 'complete';
  const unavailable = !model.current
    ? 'Wait for the current analysis to finish.'
    : !['covered', 'not_applicable'].includes(result.measurement.state) ||
        result.measurement.unresolved_python_exceptions.length
      ? 'An applicable pre-site review and resolution of its findings are still needed.'
      : result.stages.some((s) => s.state === 'awaiting_documents')
        ? 'Required ordering documents are still missing.'
        : open.length
          ? `${open.length} ${open.length === 1 ? 'issue still needs' : 'issues still need'} a review decision.`
          : '';
  return (
    <Paper withBorder p="md" radius="md">
      <Stack gap="sm">
        <Group justify="space-between">
          <Text fw={600}>Order review</Text>
          <Badge variant="light" color={completed ? 'blue' : 'gray'}>
            {completed
              ? 'Human review complete'
              : 'Not completed by a reviewer'}
          </Badge>
        </Group>
        <Text size="sm" c="dimmed">
          Records your review of this order. The analysis result and
          manufacturer hold remain separate.
        </Text>
        {completed && (
          <Text size="sm">
            {order.review_completion!.note} ·{' '}
            {order.review_completion!.actor_name ?? 'Reviewer'}
          </Text>
        )}
        {!!unavailable && !completed && <Text size="sm">{unavailable}</Text>}
        {!editing && (
          <Group>
            <Button
              variant="default"
              size="sm"
              disabled={!!unavailable && !completed}
              onClick={() => {
                setEditingBasis(
                  JSON.stringify([
                    data.project.latest_run_id,
                    order.review_completion?.id,
                    open.map((e) => [e.id, e.last_decision_id, e.updated_at]),
                  ]),
                );
                setEditing(true);
                setError('');
              }}
            >
              {completed ? 'Reopen order review' : 'Complete order review'}
            </Button>
          </Group>
        )}
        {editing && (
          <>
            <Text size="sm">
              PO {order.identity.po_number ?? order.order_ref} ·{' '}
              {
                data.workflow.exceptions.filter(
                  (e) => e.order_id === order.id && e.state === 'resolved',
                ).length
              }{' '}
              closed follow-ups · Original analysis:{' '}
              {result.verification === 'verified' ? 'Verified' : 'Not verified'}
            </Text>
            <Textarea
              label="Review summary"
              required
              value={note}
              onChange={(e) => setNote(e.currentTarget.value)}
              maxLength={4000}
            />
            <Group>
              <Button
                loading={busy}
                disabled={!note.trim() || (!!unavailable && !completed)}
                onClick={async () => {
                  setBusy(true);
                  setError('');
                  try {
                    if (
                      editingBasis !==
                      JSON.stringify([
                        data.project.latest_run_id,
                        order.review_completion?.id,
                        open.map((e) => [
                          e.id,
                          e.last_decision_id,
                          e.updated_at,
                        ]),
                      ])
                    )
                      throw new Error(
                        'The order changed. Cancel and review the latest decisions before completing.',
                      );
                    await fetchJson(
                      `/api/projects/${data.project.st_project_id}/orders/${order.id}/review`,
                      {
                        method: 'POST',
                        body: JSON.stringify({
                          run_id: model.run!.id,
                          action: completed ? 'reopen' : 'complete',
                          note,
                          expected_decision_id:
                            order.review_completion?.id ?? null,
                        }),
                      },
                      z.unknown(),
                    );
                    setEditing(false);
                    setNote('');
                    await revalidator.revalidate();
                  } catch (e) {
                    setError(
                      e instanceof Error
                        ? e.message
                        : 'Could not save the order review.',
                    );
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {completed ? 'Reopen order review' : 'Confirm completion'}
              </Button>
              <Button
                variant="subtle"
                disabled={busy}
                onClick={() => setEditing(false)}
              >
                Cancel
              </Button>
            </Group>
          </>
        )}
        {error && <Alert color="red">{error}</Alert>}
      </Stack>
    </Paper>
  );
}
